/* Generated from Java with JSweet 2.3.7 - http://www.jsweet.org */
import { Document } from '../models/Document';
import { ExtensibleNode } from '../models/ExtensibleNode';
import { Extension } from '../models/Extension';
import { IIndexedNode } from '../models/IIndexedNode';
import { IVisitable } from '../models/IVisitable';
import { Node } from '../models/Node';
import { ValidationProblem } from '../models/ValidationProblem';
import { Contact } from '../models/common/Contact';
import { ExternalDocumentation } from '../models/common/ExternalDocumentation';
import { IDefinition } from '../models/common/IDefinition';
import { Info } from '../models/common/Info';
import { License } from '../models/common/License';
import { OAuthFlow } from '../models/common/OAuthFlow';
import { OAuthFlows } from '../models/common/OAuthFlows';
import { Operation } from '../models/common/Operation';
import { Parameter } from '../models/common/Parameter';
import { Schema } from '../models/common/Schema';
import { SecurityRequirement } from '../models/common/SecurityRequirement';
import { SecurityScheme } from '../models/common/SecurityScheme';
import { Tag } from '../models/common/Tag';

/**
 * C'tor.
 * @param {*} visitor
 * @class
 * @author eric.wittmann@gmail.com
 */
export class Traverser implements ITraverser, IVisitor {
    visitor : IVisitor;

    public constructor(visitor : IVisitor) {
        if(this.visitor===undefined) this.visitor = null;
        this.visitor = visitor;
    }

    /**
     * Traverse the items of the given array.
     * @param {Bound[]} items
     */
    traverseCollection(items : Array<any>) {
        if(items != null) {
            items.forEach((node) => {
                this.traverseIfNotNull(node);
            });
        }
    }

    /**
     * Traverse the extension nodes, if any are found.
     * @param {ExtensibleNode} node
     */
    traverseExtensions(node : ExtensibleNode) {
        this.traverseCollection(node.getExtensions());
    }

    /**
     * Traverse all children of the indexed node.
     * @param {*} node
     */
    traverseIndexedNode(node : IIndexedNode<any>) {
        this.traverseCollection(node.getItems());
    }

    /**
     * Traverse the validation problems, if any are found.
     * @param {Node} node
     */
    traverseValidationProblems(node : Node) {
        this.traverseCollection(node.getValidationProblems());
    }

    /**
     * Called to traverse the data model starting at the given node and traversing
     * down until this node and all child nodes have been visited.
     * @param {Node} node
     */
    public traverse(node : Node) {
        node.accept(this);
    }

    /**
     * Traverse into the given node, unless it's null.
     * @param {*} node
     */
    traverseIfNotNull(node : IVisitable) {
        if(node != null) {
            node.accept(this);
        }
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitDocument(io.apicurio.datamodels.core.models.Document)
     * @param {Document} node
     */
    public visitDocument(node : Document) {
        node.accept(this.visitor);
        this.traverseDocument(node);
    }

    /**
     * Subclasses can override this to provide version-specific traversal of the root document.
     * @param {Document} node
     */
    traverseDocument(node : Document) {
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
        this.traverseIfNotNull(node.info);
        this.traverseCollection(node.tags);
        this.traverseIfNotNull(node.externalDocs);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitExtension(io.apicurio.datamodels.core.models.Extension)
     * @param {Extension} node
     */
    public visitExtension(node : Extension) {
        node.accept(this.visitor);
        this.traverseValidationProblems(node);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitValidationProblem(io.apicurio.datamodels.core.models.ValidationProblem)
     * @param {ValidationProblem} problem
     */
    public visitValidationProblem(problem : ValidationProblem) {
        problem.accept(this.visitor);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitInfo(io.apicurio.datamodels.core.models.common.Info)
     * @param {Info} node
     */
    public visitInfo(node : Info) {
        node.accept(this.visitor);
        this.traverseIfNotNull(node.contact);
        this.traverseIfNotNull(node.license);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitContact(io.apicurio.datamodels.core.models.common.Contact)
     * @param {Contact} node
     */
    public visitContact(node : Contact) {
        node.accept(this.visitor);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitLicense(io.apicurio.datamodels.core.models.common.License)
     * @param {License} node
     */
    public visitLicense(node : License) {
        node.accept(this.visitor);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitTag(io.apicurio.datamodels.core.models.common.Tag)
     * @param {Tag} node
     */
    public visitTag(node : Tag) {
        node.accept(this.visitor);
        this.traverseIfNotNull(node.externalDocs);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitSecurityRequirement(io.apicurio.datamodels.core.models.common.SecurityRequirement)
     * @param {SecurityRequirement} node
     */
    public visitSecurityRequirement(node : SecurityRequirement) {
        node.accept(this.visitor);
        this.traverseValidationProblems(node);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitExternalDocumentation(io.apicurio.datamodels.core.models.common.ExternalDocumentation)
     * @param {ExternalDocumentation} node
     */
    public visitExternalDocumentation(node : ExternalDocumentation) {
        node.accept(this.visitor);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitSchema(io.apicurio.datamodels.core.models.common.Schema)
     * @param {Schema} node
     */
    public visitSchema(node : Schema) {
        node.accept(this.visitor);
        this.traverseSchema(node);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }

    traverseSchema(node : Schema) {
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitParameter(io.apicurio.datamodels.core.models.common.Parameter)
     * @param {Parameter} node
     */
    public visitParameter(node : Parameter) {
        node.accept(this.visitor);
        this.traverseParameter(node);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }

    traverseParameter(node : Parameter) {
        this.traverseIfNotNull(node.schema);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitParameterDefinition(io.apicurio.datamodels.core.models.common.IDefinition)
     * @param {*} node
     */
    public visitParameterDefinition(node : IDefinition) {
        this.visitParameter(<Parameter><any>node);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitOperation(io.apicurio.datamodels.core.models.common.Operation)
     * @param {Operation} node
     */
    public visitOperation(node : Operation) {
        node.accept(this.visitor);
        this.traverseOperation(node);
    }

    traverseOperation(node : Operation) {
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
        this.traverseIfNotNull(node.externalDocs);
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitSecurityScheme(io.apicurio.datamodels.core.models.common.SecurityScheme)
     * @param {SecurityScheme} node
     */
    public visitSecurityScheme(node : SecurityScheme) {
        node.accept(this.visitor);
        this.traverseSecurityScheme(node);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }

    traverseSecurityScheme(node : SecurityScheme) {
    }

    /**
     * @see io.apicurio.datamodels.core.visitors.IVisitor#visitSchemaDefinition(io.apicurio.datamodels.core.models.common.IDefinition)
     * @param {*} node
     */
    public visitSchemaDefinition(node : IDefinition) {
        let schema : Schema = <Schema><any>node;
        this.visitSchema(schema);
    }

    /**
     * Traverse an OAuth flow.
     * @param {OAuthFlow} node
     */
    traverseOAuthFlow(node : OAuthFlow) {
        node.accept(this.visitor);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }

    /**
     * Traverses an oauth flows node.
     * @param {OAuthFlows} node
     */
    traverseOAuthFlows(node : OAuthFlows) {
        node.accept(this.visitor);
        this.traverseIfNotNull(node.implicit);
        this.traverseIfNotNull(node.password);
        this.traverseIfNotNull(node.clientCredentials);
        this.traverseIfNotNull(node.authorizationCode);
        this.traverseExtensions(node);
        this.traverseValidationProblems(node);
    }
}
Traverser["__class"] = "io.apicurio.datamodels.core.visitors.Traverser";
Traverser["__interfaces"] = ["io.apicurio.datamodels.core.visitors.IVisitor","io.apicurio.datamodels.core.visitors.ITraverser"];





import { IVisitor } from './IVisitor';


import { ITraverser } from './ITraverser';

